import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UpdateCompanyDto, UserProfileDto, UserRole } from "@green0meter/api-client";

import { DOMAIN_COMPANIES } from "../domain";
import { ACTION_GET_ADMIN_COMPANIES } from "../GetAdminCompanies/useGetAdminCompanies";

import { companiesAdminApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_UPDATE_ADMIN_COMPANY = "UPDATE_ADMIN_COMPANY";

const allowedRoles = [UserRole.Admin];

interface UpdateAdminCompanyRequest {
  companyId: string;
  data: UpdateCompanyDto;
}

const updateAdminCompany = async (
  params: UpdateAdminCompanyRequest,
): Promise<void> => {
  await companiesAdminApi.companiesAdminControllerUpdateAdminCompany(
    params.companyId,
    params.data,
  );
};

export const useUpdateAdminCompany = (
  user: UserProfileDto | null,
): {
  updateAdminCompany: (params: UpdateAdminCompanyRequest) => void;
  updateAdminCompanyAsync: (params: UpdateAdminCompanyRequest) => Promise<void>;
  updateAdminCompanyEnabled: boolean;
  isSuccessUpdateAdminCompany: boolean;
  isPendingUpdateAdminCompany: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_COMPANIES, ACTION_UPDATE_ADMIN_COMPANY],
    mutationFn: enabled ? updateAdminCompany : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_ADMIN_COMPANIES],
      });
    },
  });

  return {
    updateAdminCompany: mutate,
    updateAdminCompanyAsync: mutateAsync,
    updateAdminCompanyEnabled: enabled,
    isSuccessUpdateAdminCompany: isSuccess,
    isPendingUpdateAdminCompany: isPending,
  };
};
